import React from 'react';
import { useTranslation } from 'react-i18next';
import ParcelInfo from 'components/generics/Map/ParcelInfo';
import { emptyCulture } from 'utils/culturesColors';
import './Map.style.scss';
import MapTab from './MapTab';
import MapInteractionHook from './MapInteraction.hook';

function MapResults({
  data,
  parcelUpdated,
  setModalData,
  nbYear,
  setMapYear,
  center,
  suggestions,
  previousCropMargin,
  forecastCampaign,
  selectedYear,
}) {
  const { t } = useTranslation();
  const minYear = forecastCampaign.startYear;
  const maxYear = forecastCampaign.startYear + nbYear;

  const getCultureForParcel = (parcel, year) => {
    let culture = parcel.previousCulture1;
    if (!culture?.name) {
      culture = { ...emptyCulture, year: minYear };
    }
    for (let i = 0; i < parcel.cultures.length; i++) {
      const nextYear = minYear + (i + 1);
      if (nextYear === year) {
        culture = parcel.cultures[i];
      }
    }
    return culture;
  };

  const hasIntercultureForYear = (culture) => {
    if (Array.isArray(culture)) {
      return culture.some((c) => c.type === 'intermediate');
    }
    return culture?.type === 'intermediate';
  };

  const isSplitParcel = (culture) => {
    if (Array.isArray(culture)) {
      return culture?.find((cultureProp) => cultureProp?.isSplit || false);
    } else {
      return false;
    }
  };

  const getPolygonStyleParams = (parcel, culture, opacity) => {
    const interculture = Array.isArray(culture)
      ? culture.find((c) => c.type === 'intermediate')
      : culture?.type === 'intermediate'
        ? culture
        : null;

    return {
      color: Array.isArray(culture) ? culture : (culture?.name ?? culture),
      opacity,
      organicState: Array.isArray(culture) ? culture[0]?.organicState : culture?.organicState,
      hasInterculture: !!interculture,
      intercultureColor: interculture?.name,
      isSplit: isSplitParcel(culture),
    };
  };

  const onParcelClick = (parcel, parcelYear) => {
    if (parcel) {
      setModalData(parcel, parcelYear, minYear);
    }
  };

  const mapState = MapInteractionHook({
    mapId: 'map',
    parcelsData: data || [],
    initialYear: forecastCampaign.startYear + 1,
    minYear,
    maxYear,
    getCultureForParcel,
    getPolygonStyleParams,
    onParcelClick,
    dependencies: [parcelUpdated, center],
  });

  // Sync mapYear with parent
  React.useEffect(() => {
    setMapYear(mapState.year - 1);
  }, [mapState.year]);

  const yearIndex = mapState.year - maxYear + nbYear - 1;

  return (
    <div className="tab_container">
      <div className="details_container">
        <MapTab
          data={data}
          header={['', t('common.culture'), t('common.surface'), t('column.marginPerHa')]}
          suggestions={suggestions}
          previousCropMargin={previousCropMargin}
          isDynamic={true}
          year={mapState.year}
          minYear={mapState.minYear}
          maxYear={mapState.maxYear}
          setYear={mapState.setYear}
          yearIndex={yearIndex}
          parcelHoverCultureName={mapState.parcelHoverCultureName}
          parentCallback={mapState.handleCultureHover}
          fromSynthesis={true}
        />
      </div>
      <div ref={mapState.mapElement} className="map_container">
        <div id="map" className="map_element" onMouseOut={mapState.clearPopup}></div>

        {mapState.parcelInfo && mapState.modalCoords && (
          <div ref={mapState.popup} style={mapState.getPopupStyle()}>
            <div
              ref={mapState.popupContentRef}
              className="ol-popup"
              style={mapState.getPopupInnerStyle()}
            >
              <ParcelInfo
                cultureData={mapState.parcelHoverCultureName}
                parcelInfo={mapState.parcelInfo}
                parcels={data}
                year={mapState.year}
                minYear={minYear}
                from="mapResult"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MapResults;
